import React, { useEffect, useState } from 'react';
import { FaCopy, FaCheck, FaTools, FaDownload, FaCog, FaKey, FaMapMarkedAlt } from 'react-icons/fa';
import { FaChevronCircleLeft } from 'react-icons/fa';
import './RoninInstallerGuide.css';
import ModalImageViewer from './modals/ModalImageViewer';
import infoIcon from '../../../images/installer/info-icon.svg';
import configImage from '../../../images/installer/image-1-0.png';
import configUpdateImage from '../../../images/installer/image-1-0-0.png';
import configUpdateErrorImage from '../../../images/installer/image-1-0-error.png';
import configUpdateOkImage from '../../../images/installer/image-1-0-ok.png';
import businessTokenImage from '../../../images/installer/image-1-1.png';
import validateLicenseImage from '../../../images/installer/image-1-2.png';
import LicenseProfileImage from '../../../images/installer/image-1-3.png';
import ubuntuInstallerImage from '../../../images/installer/ubuntu-installer.png';
import dockerRunImage from '../../../images/installer/docker-run.png';
import roninIcon from '../../../images/RoninIconSmall.png';
import { hmLink, application, vms, businessLink, fullBusiness, webURL } from '../../../config/index.js';
// import SideBar from '../../SideBar.js';
import SideBarSmallScreen from '../../SideBarSmallScreen.js';
// import Footer from '../../Footer.js';
import CongratsModal from './modals/congratsModal/CongratsModal.js';
// import SideBarTest from '../../SideBarNew.js';

// Import generic components
import Footer from '../../../generic_components/Footer/Footer.jsx';

const isSmallScreen = window.innerWidth < 600;

const titleToIcon = (title) => {
  switch (title) {
    case 'Prerequisites':
      return <FaTools className="step-icon" />;
    case 'Installation':
      return <FaDownload className="step-icon" />;
    case 'Configuration':
      return <FaCog className="step-icon" />;
    case 'Site Configuration':
      return <FaMapMarkedAlt className="step-icon" />;
    case 'Licensing':
      return <FaKey className="step-icon" />;
    default:
      return null;
  }
}

function CopyableCode({ code }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
      <pre
        style={{       
          color: '#fff',
          padding: '4px 12px',
          borderRadius: '6px',
          margin: 0,
          userSelect: 'all',
          fontSize: '14px',
          fontFamily: 'monospace',         
        }}
      >
       <code>{code}</code>
      </pre>
      <button
        onClick={copyToClipboard}
        className={`copy-button${copied ? ' copied' : ''}`}
        aria-label="Copy to clipboard"
      >
        {copied ? <FaCheck style={{ color: 'green' }} /> : <FaCopy />}
        <span className="copy-tooltip">{copied ? 'Copied' : 'Copy to clipboard'}</span>
      </button>
    </div>
  );
}

export default function RoninInstallerGuide({ goback }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedSteps, setCheckedSteps] = useState(Array(5).fill(false));
  const [showTerminalHelp, setShowTerminalHelp] = useState(false);
  const [showWgetHelp, setShowWgetHelp] = useState(false);
  const [showKatanaHelp, setShowKatanaHelp] = useState(false);
  const [showAdditionalDevicesHelp, setShowAdditionalDevicesHelp] = useState(false);
  const [showConfigImageModal, setShowConfigImageModal] = useState(false);
  const [showBusinessTokenImageModal, setShowBusinessTokenImageModal] = useState(false);
  const [showValidateLicenseImageModal, setShowValidateLicenseImageModal] = useState(false);
  const [showLicenseProfileImageModal, setShowLicenseProfileImageModal] = useState(false);
  const [showDockerRunImageModal, setShowDockerRunImageModal] = useState(false);
  const [showConfigUpdateModal, setShowConfigUpdateModal] = useState(false);
  const [showConfigUpdateErrorModal, setShowConfigUpdateErrorModal] = useState(false);
  const [showConfigUpdateOkModal, setShowConfigUpdateOkModal] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showUbuntuInstallerImageModal, setShowUbuntuInstallerImageModal] = useState(false);

  const gobackLink = goback === 'faq' ? '/#faq' : '/#health';


  const steps = [
    {
      title: 'Prerequisites',
      content: (
        <ul>
           <li>• Linux (Ubuntu on AMD64/ARM64/AARCH64) or Windows (x86/x64) systems.</li>
           <li>• {vms} VMS Server is installed and running on your machine</li>
          <li>
            • Access to the <span className="highlight">Terminal</span> on your machine
            <span
              onClick={() => setShowTerminalHelp(!showTerminalHelp)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setShowTerminalHelp(!showTerminalHelp);
              }}
              aria-label="Toggle terminal access help"
            > 
          <img className="info-icon" src={infoIcon} alt="info" />
            </span>
            {showTerminalHelp && (
              <div className="info-container">
               <p>
                 🔍 Click the <code>Show Applications</code> icon (usually bottom-left), search for{' '}
                    <code>Terminal</code>, and open it.
               </p>
              </div>
            )}
          </li>
          <li>
            • <span className="highlight">wget</span> command-line tool is required to download the installation script
            <span
              onClick={() => setShowWgetHelp(!showWgetHelp)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setShowWgetHelp(!showWgetHelp);
              }}
              aria-label="Toggle wget help"
            >
            <img className="info-icon" src={infoIcon} alt="info" />
              </span>
              {showWgetHelp && (
                <div className="info-container">
                  <ul>
                    <li className="subsection">
                     <div>
                       – To check if <span className="highlight">wget</span> is installed, run the following command in the terminal:
                     </div>                     
                      <div>
                        <CopyableCode code="wget --version" />
                      </div>                      
                      <span className="note">
                        ✔️ If installed, you will see output like: <code>wget: GNU Wget 1.21.x</code>
                      </span>
                    </li>
                    <br />
                    <li className="subsection">
                      – If not installed, you will see a message like: <code>command not found</code> or similar.
                    <li/>
                    <li className="subsection">
                      – To install it, run the following command in the terminal:
                    </li>                     
                      <CopyableCode code="sudo apt install wget -y" />                     
                    </li>
                  </ul>
                </div>
              )}
          </li>
          <li>• You need administrator access (called ‘sudo’) to set up and run Docker on this system</li>
          <li>• <span className="highlight">Docker</span> will be installed in the following section if not already present</li>
         
        </ul>
      ),
    },
    {
      title: 'Installation',
      content: (
        <ul>
          <li className="subsection"><strong>• For Linux (Ubuntu):</strong></li>
          <ul>
            
              <li className='subsection'>
                <strong>• </strong>Installing via Ubuntu Software (GUI Method):
                <ul>
                  <li className="subsection">
                    Click the link below to download the RONIN installer:
                  </li>
                  <li className="subsection" style={{ marginLeft: isSmallScreen ? '-4.4em' : 0 }}>
                    <a href={`${hmLink}/ronin.deb`} target="_blank" rel="noopener noreferrer">
                      RONIN Ubuntu Installer
                    </a>
                  </li>
                  <li className="subsection">
                    Right-click the downloaded (ronin.deb) file, click Open With <code>Other Application</code>, then select <code>Software Install</code> to begin the installation.
                    <span
                    className="image-toggle"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowUbuntuInstallerImageModal(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') setShowUbuntuInstallerImageModal(true);
                    }}
                  >
                  {' '}view screenshot
                  </span>
                  </li>
                </ul>
                <li className='subsection'>
                  <strong>• </strong>Installing via Terminal (Command Line Method):
                </li>
             <ul>
               <li className="subsection">
                Copy the following command into your device’s terminal and press Enter
              </li>         
          
              <li className="subsection" style={{ marginLeft: isSmallScreen ? '-4.4em' : 0 }}>
                <CopyableCode code={`wget -qO- ${hmLink}/ronin.sh | bash`} />
              </li>
              <li className="subsection">
                You will see an output similar to the one shown in {' '}
                <span
                    className="image-toggle"
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowDockerRunImageModal(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') setShowDockerRunImageModal(true);
                    }}
                  >
                  view screenshot
                  </span>
              </li>   
             </ul>
            </li>              
             
          </ul>
          <li className="subsection"><strong>• For Windows:</strong></li>
           <ul>
            <li className="subsection">
              Click the link below to download the RONIN installer:
            </li>
            <li className="subsection" style={{ marginLeft: isSmallScreen ? '-4.4em' : 0 }}>
             <a href={`${hmLink}/ronin_installer.zip`} target="_blank" rel="noopener noreferrer">
                RONIN Windows Installer
              </a>
            </li>
            <li className="subsection">            
              Extract the ZIP, then run ronin.exe and follow the on-screen prompts.
            </li>
            <li className='subsection'>
              <p>
                <strong>Note: </strong>
                Windows may display a security warning during installation.
                Our installer is safe to run and does not contain any harmful software.
                Click “Run anyway” or “Allow” when prompted to proceed.
              </p>
            </li>
          </ul>
        </ul>
      ),
    },
    {
      title: 'Configuration',
      content: (
        <>
          <p>
            Access the configuration Interface{' '}
            <span
              className="image-toggle"
              role="button"
              tabIndex={0}
              onClick={() => setShowConfigImageModal(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setShowConfigImageModal(true);
              }}
            >
            (view screenshot)
          </span>
        </p>

          <ul>
            <li className="subsection">Open a web browser and enter the following in the address bar, then press Enter:</li>
            <li className="subsection">• Same device:</li>
            <ul>
               <li className="subsection"><CopyableCode code="http://127.0.0.1:47274" /></li>
            </ul>
            <li className="subsection">• To access the device from another device on the same network:</li>
            <ul>              
               <li className="subsection"><CopyableCode code="http://[DEVICE_IP]:47274" /></li>
               <li className="subsection">Replace [DEVICE_IP] with the actual IP address of the device you're trying to reach</li>
            </ul>
            <ul>
              <li><code>Example: http://192.168.1.12:47274</code></li>
            </ul>
          </ul>
          <p>
            <strong>1.</strong> NX Server Setup
          </p>
          <ul>
            <li className="subsection">• Nx IP address: 127.0.0.1 or local network IP</li>
            <li className="subsection">• Nx Port: Either 7011 or 7001 </li>
            <li className="subsection">• Nx Credentials: Provide admin-level username and password</li>
          </ul>
          <p>
            <strong>2.</strong> Optional Configuration
          </p>
          <ul>
            <li className="subsection">• Katana Switches (Optional)
              <span
                onClick={() => setShowKatanaHelp(!showKatanaHelp)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowKatanaHelp(!showKatanaHelp);
                }}
                aria-label="Toggle Katana Switches help"
              >
                <img className="info-icon" src={infoIcon} alt="info" />
              </span>
              {showKatanaHelp && (
                <div className="info-container">
                  <ul>
                    <li className="subsection">
                      – Check the checkbox if you have Katana switches
                    </li>
                    <li className="subsection">
                      – Provide IP address, username, and password
                    </li>
                  </ul>
                </div>
              )}
            </li>
            <li className="subsection">• Additional IP Devices (Optional)
              <span
                onClick={() => setShowAdditionalDevicesHelp(!showAdditionalDevicesHelp)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowAdditionalDevicesHelp(!showAdditionalDevicesHelp);
                }}
                aria-label="Toggle Additional IP Devices help"
              >
                <img className="info-icon" src={infoIcon} alt="info" />
              </span>
              {showAdditionalDevicesHelp && (
                <div className="info-container">
                  <ul>
                    <li className="subsection">
                      – Add UPS or other network devices
                    </li>
                    <li className="subsection">
                      – Provide device name and IP address
                    </li>
                  </ul>
                </div>
              )}
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Site Configuration',
      content: (
        <>
          <p>
            <strong>3.</strong> Business Token
          </p>
          <ul>
            <li className='subsection'>• Enter your Business Token</li>        
            <li className='subsection'>• Click your user icon (top right) to find the token {' '}
              <span
                className="image-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowBusinessTokenImageModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowBusinessTokenImageModal(true);
                }}
              >
                (view screenshot)
              </span>
            </li>
          </ul>
          <p>
            <strong>4.</strong> Site Information
          </p>
          <ul>
            <li className='subsection'>• Enter a unique site name</li>
            <li className='subsection'>• Enter the System Integrator's name</li>
          </ul>
          <p>
            <strong>5.</strong> Location Setup
          </p>
          <ul>
            <li className='subsection'>• Drag the map pin or enter latitude/longitude manually</li>
          </ul>
          <p>
            <strong>6.</strong> Click Update Config to save {' '}
            <span
              className="image-toggle"
              role="button"
              tabIndex={0}
              onClick={() => setShowConfigUpdateModal(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setShowConfigUpdateModal(true);
              }}
            >
              (view screenshot)
            </span>
          </p>
          <ul>
            <li className='subsection'>• If you see an error, check the configuration and try again {' '}
              <span
                className="image-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowConfigUpdateErrorModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowConfigUpdateErrorModal(true);
                }}
              >
                (view screenshot)
              </span>
            </li>
            <li className='subsection'>• If successful, you will see a confirmation message {' '}
              <span
                className="image-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowConfigUpdateOkModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowConfigUpdateOkModal(true);
                }}
              >
                (view screenshot)
              </span>
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Licensing',
      content: (
        <>
          <p>
            <strong>Activate License</strong>
          </p>
          <ul>
            <li className='subsection'>
              <strong>1.</strong> Go to Validate Licensing in top menu {' '}
              <span
                className="image-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowValidateLicenseImageModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowValidateLicenseImageModal(true);
                }}
              >
                <span className="image-link">(view screenshot)</span>
              </span>
            </li>
            <li className='subsection'>
              <strong>2.</strong> Enter your purchased license key</li>
            <li className='subsection'>
              <strong>3.</strong> Validate the key
            </li>
            <li className='subsection'>
              <strong>4.</strong> View active licenses in the Licensing Profile tab {' '}
              <span
                className="image-toggle"
                role="button"
                tabIndex={0}
                onClick={() => setShowLicenseProfileImageModal(true)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setShowLicenseProfileImageModal(true);
                }}
              >
                <span className="image-link">(view screenshot)</span>
              </span>
            </li>
            <li className='subsection'>
              <strong>5.</strong> Site appears on the{' '}
              <a href="/#health">
                {application.toUpperCase()}
              </a>{' '}
              Portal within 5 minutes of license activation.
            </li>
          </ul>
          <p>
            <strong>Important Notes</strong>
          </p>
          <ul>
            <li className='subsection'>• License must cover the total number of cameras on the individual NX server</li>
            <li className='subsection'>• Purchase licenses from {' '}
              <a href={businessLink} target="_blank" rel="noopener noreferrer">
                {fullBusiness}
              </a>
            </li>
            
            <li className='subsection'>•Support Contact: </li>
            <ul>
              <li className='subsection'>📧  sales@artoflogictech.com</li>
              <li className='subsection'>📞  1300 300 123</li>
            </ul>
          </ul>
        </>
      ),
    },
  ];

  const handleCheckbox = (index) => {
    const updated = [...checkedSteps];
    updated[index] = !updated[index];
    setCheckedSteps(updated);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1 && checkedSteps[currentStep]) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = steps.length
    ? (checkedSteps.filter(Boolean).length / steps.length) * 100
    : 0;

  const isFinished = (currentStep === steps.length - 1) && (checkedSteps.filter(Boolean).length === steps.length);

  // Show confetti when the installation is finished
  useEffect(() => {
    if (isFinished) {
      setShowCongrats(true);
      const timer = setTimeout(() => setShowCongrats(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isFinished]);

  return (
    <>
    {/* {
      !isSmallScreen &&  
      (
        <div className='sidebar-container'>
            <SideBar />
        </div>
      )
    } */}
    <div className="ronin-main-container">
      <div className="go-back">
        <a href={gobackLink} className="go-back-link">
         <FaChevronCircleLeft className="w-5 h-5" /> BACK
        </a>
      </div>
      <div className="ronin-wrapper">
        <div className="ronin-container">
          <div className="ronin-intro">
            <div className='ronin-intro-title'>Welcome to the {application.toUpperCase()} Installer 
              { !isSmallScreen && (
                <span>
                Guide
              <img alt='' src={roninIcon} className="ronin-intro-title-icon"/>
              </span>
              )}
            </div>
            <p>
              This guide walks you through the step-by-step installation and setup process for {application.toUpperCase()}.
              Each section must be reviewed and marked as complete before continuing to the next step.
            </p>
          </div>

          <div className="ronin-card">
            <label className="ronin-checkbox">
              <input
                type="checkbox"
                checked={checkedSteps[currentStep]}
                onChange={() => handleCheckbox(currentStep)}
              />
              <span className="ronin-title">
                {steps[currentStep].title} {' '} 
                <span className="ronin-title-icon">
                  {titleToIcon(steps[currentStep].title) ? titleToIcon(steps[currentStep].title) : null}
                </span>
              </span>
            </label>

            <div className="ronin-content">{steps[currentStep].content}</div>

            <div className="ronin-buttons">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="ronin-button"
              >
                Previous
              </button>
              <button
                onClick={handleNext}
                disabled={!checkedSteps[currentStep]}
                aria-disabled={!checkedSteps[currentStep]}
                className={`ronin-button 
                            ${!checkedSteps[currentStep] ? 'disabled' : ''} 
                            ${isFinished ? 'green-theme' : ''}`}
              >
                {isFinished ? 'Finish' : 'Next'}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="ronin-progress-container">
              <div className="ronin-progress-bar" style={{ width: `${progress}%` }}></div>
              <div className="ronin-progress-text">
                {checkedSteps.filter(Boolean).length} / {steps.length} steps completed
              </div>
            </div>
          </div>
        </div>

        {/* Modals for Config page */}
        {showConfigImageModal && (
          <ModalImageViewer
            src={configImage}
            alt="Configuration Interface Screenshot"
            onClose={() => setShowConfigImageModal(false)}
          />
        )}

        {/* Modals for Business token page */}
        {showBusinessTokenImageModal && (
          <ModalImageViewer
            src={businessTokenImage}
            alt="Business Token Screenshot"
            onClose={() => setShowBusinessTokenImageModal(false)}
          />
        )}
        {/* Modals for Validate License page */}
        {showValidateLicenseImageModal && (
          <ModalImageViewer
            src={validateLicenseImage}
            alt="Validate License Screenshot"
            onClose={() => setShowValidateLicenseImageModal(false)}
          />
        )}

        {/* Modals for License Profile page */}
        {showLicenseProfileImageModal && (
          <ModalImageViewer
            src={LicenseProfileImage}
            alt="License Profile Screenshot"
            onClose={() => setShowLicenseProfileImageModal(false)}
          />
        )}   

        {/* Modals for Docker Run page */}
        {showDockerRunImageModal && (
          <ModalImageViewer
            src={dockerRunImage}
            alt="Docker Run Screenshot"
            onClose={() => setShowDockerRunImageModal(false)}
          />
        )}

        {/* Modal for congratulation */}
        <CongratsModal show={showCongrats} onClose={() => setShowCongrats(false)} /> 

        {/* Modal for Config Update */}
        {showConfigUpdateModal && (
          <ModalImageViewer
            src={configUpdateImage}
            alt="Config Update Screenshot"
            onClose={() => setShowConfigUpdateModal(false)}
          />
        )}

        {/* Modal for Config Update Error */}
        {showConfigUpdateErrorModal && (
          <ModalImageViewer
            src={configUpdateErrorImage}
            alt="Config Update Error Screenshot"
            onClose={() => setShowConfigUpdateErrorModal(false)}
          />
        )}

        {/* Modal for Config Update Ok */}
        {showConfigUpdateOkModal && (
          <ModalImageViewer
            src={configUpdateOkImage}
            alt="Config Update Ok Screenshot"
            onClose={() => setShowConfigUpdateOkModal(false)}
          />
        )}

        {/* Modal for Ubuntu Installer Image */}
        {showUbuntuInstallerImageModal && (
          <ModalImageViewer
            src={ubuntuInstallerImage}
            alt="Ubuntu Installer Screenshot"
            onClose={() => setShowUbuntuInstallerImageModal(false)}
          />
        )}

      </div>
    </div>

    {/* side bar cards for small screens  */}
        {isSmallScreen && <SideBarSmallScreen/>}

        <div className='installer-footer'>
            <Footer url={webURL}/> 
        </div>
    </>
  );
}
